import { userTagCanAssign, type UserTag } from '@/store/userTag';
import type { ViewingTagSelection } from '@/utils/viewingTagSelection';

/**
 * Returns the UserTag that matches the current TagSelectorDropdown value,
 * regardless of entitlement. Used to prefill the Add Account Tags field.
 */
export function resolveSelectedViewingTag(
  selection: ViewingTagSelection,
  userTags: readonly UserTag[] | undefined,
): UserTag | null {
  if (selection === null || !userTags?.length) {
    return null;
  }
  return (
    userTags.find((candidate) => candidate.tagId === selection.tagId) ?? null
  );
}

/**
 * Returns the currently filtered viewing tag when the user may assign accounts
 * to it (Assign or Edit on GET /api/user/tags). Firm/read-only tags are skipped.
 */
export function resolveAttributableViewingTag(
  selection: ViewingTagSelection,
  userTags: readonly UserTag[] | undefined,
): UserTag | null {
  const tag = resolveSelectedViewingTag(selection, userTags);
  if (!tag || !userTagCanAssign(tag)) {
    return null;
  }
  return tag;
}

/**
 * Selected personal/firm user tags the caller may add accounts to (Assign or
 * Edit). Order follows `selectedTagIds`; unknown and read-only ids are omitted.
 */
export function resolveAssignableUserTags(
  selectedTagIds: readonly number[],
  userTags: readonly UserTag[] | undefined,
): UserTag[] {
  if (!userTags?.length || selectedTagIds.length === 0) {
    return [];
  }
  const byId = new Map(userTags.map((tag) => [tag.tagId, tag]));
  const seen = new Set<number>();
  const assignable: UserTag[] = [];
  for (const id of selectedTagIds) {
    if (!Number.isFinite(id) || seen.has(id)) {
      continue;
    }
    const tag = byId.get(id);
    if (!tag || !userTagCanAssign(tag)) {
      continue;
    }
    seen.add(id);
    assignable.push(tag);
  }
  return assignable;
}

export { uniqueFiniteTagIds } from '@/modules/portfolio/accountModal/accountFormTags';

/**
 * One-shot seed for Add Account Tags from the current viewing-tag filter.
 * Returns `nextIds` when the empty field should be prefilled; otherwise null.
 */
export function nextSelectedTagIdsForViewingTagSeed(args: {
  hasSeeded: boolean;
  viewingTagId: number | null;
  currentIds: number[];
}): { nextIds: number[] | null; seeded: boolean } {
  if (args.hasSeeded) {
    return { nextIds: null, seeded: true };
  }
  if (args.viewingTagId == null) {
    return { nextIds: null, seeded: false };
  }
  if (
    args.currentIds.includes(args.viewingTagId) ||
    args.currentIds.length > 0
  ) {
    return { nextIds: null, seeded: true };
  }
  return { nextIds: [args.viewingTagId], seeded: true };
}

/** Merges account ids into a user-tag WealthAccountIds list (stable, de-duped). */
export function mergeWealthAccountIds(
  existingIds: readonly number[],
  accountIdsToAdd: readonly number[],
): number[] {
  const merged = new Set<number>();
  for (const id of existingIds) {
    if (Number.isFinite(id)) {
      merged.add(id);
    }
  }
  for (const id of accountIdsToAdd) {
    if (Number.isFinite(id)) {
      merged.add(id);
    }
  }
  return Array.from(merged);
}

/** Drops account ids from a user-tag WealthAccountIds list, preserving order. */
export function removeWealthAccountIds(
  existingIds: readonly number[],
  accountIdsToRemove: readonly number[],
): number[] {
  const remove = new Set(
    accountIdsToRemove.filter((id) => Number.isFinite(id)),
  );
  return existingIds.filter((id) => Number.isFinite(id) && !remove.has(id));
}

/**
 * Assignable personal/firm tags gained or lost between two account tag
 * selections. Order follows the input lists. Account-group ids and tags the
 * user cannot assign are ignored.
 */
export function diffAssignableUserTagMembership(
  previousTagIds: readonly number[],
  nextTagIds: readonly number[],
  userTags: readonly UserTag[] | undefined,
): { added: UserTag[]; removed: UserTag[] } {
  const previous = resolveAssignableUserTags(previousTagIds, userTags);
  const next = resolveAssignableUserTags(nextTagIds, userTags);
  const previousIds = new Set(previous.map((tag) => tag.tagId));
  const nextIds = new Set(next.map((tag) => tag.tagId));
  return {
    added: next.filter((tag) => !previousIds.has(tag.tagId)),
    removed: previous.filter((tag) => !nextIds.has(tag.tagId)),
  };
}

/** Account ids present in `after` but not in `before` (Yodlee link attribution). */
export function diffNewAccountIds(
  beforeIds: readonly number[],
  afterIds: readonly number[],
): number[] {
  const before = new Set(beforeIds.filter((id) => Number.isFinite(id)));
  return afterIds.filter((id) => Number.isFinite(id) && !before.has(id));
}

/**
 * Diffs post-link vs pre-link account ids. `null` on either side means that
 * snapshot failed — callers must skip attribution rather than treat a failed
 * pre-link list as an empty portfolio.
 */
export function resolveLinkedAccountIdsForAttribution(
  beforeIds: readonly number[] | null,
  afterIds: readonly number[] | null,
): number[] | null {
  if (beforeIds == null || afterIds == null) {
    return null;
  }
  return diffNewAccountIds(beforeIds, afterIds);
}
